
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Services() {
  useEffect(() => {
    // Add SEO meta tags dynamically
    document.title = 'Demolition Services - Residential, Commercial & Laboratory | Central Demolition';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional demolition services including residential, commercial, and specialized laboratory demolition across New England. Licensed, insured, and safety-focused.');
    }

    // Add Schema.org JSON-LD for Services page
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Professional Demolition Services",
      "description": "Comprehensive demolition services for residential, commercial, and laboratory projects",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/services`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Central Demolition & Services",
        "telephone": "781-408-9479",
        "email": "contact@central-demolition.com"
      },
      "areaServed": [
        "Massachusetts",
        "Rhode Island",
        "Connecticut", 
        "New Hampshire",
        "Vermont",
        "Maine"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Demolition Services Catalog",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Residential Demolition",
              "description": "Complete home demolition services with neighborhood consideration",
              "serviceType": "Demolition"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Commercial Demolition",
              "description": "Commercial and industrial demolition projects",
              "serviceType": "Demolition"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Laboratory Demolition", 
              "description": "Specialized laboratory and sensitive equipment demolition",
              "serviceType": "Specialized Demolition"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "General Labor",
              "description": "Professional labor services for construction projects",
              "serviceType": "Construction Labor"
            }
          }
        ]
      }
    });
    document.head.appendChild(schemaScript);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Services — <span className="text-blue-600">Clean</span>, <span className="text-blue-600">Safe</span>, and <span className="text-blue-600">Expertly Done</span>.
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From residential demolitions to specialized laboratory services, we deliver 
              precise, safe, and efficient solutions across all of New England.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            
            {/* Demolition Services */}
            <div className="fade-in-on-scroll">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Demolition Services</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Professional demolition services for all types of structures, 
                  executed with precision and safety.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="w-full h-48 bg-blue-50 flex items-center justify-center">
                    <i className="ri-home-4-line text-6xl text-blue-600"></i>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">Residential Demolition</h3>
                    <p className="text-gray-600 mb-4">
                      Complete home demolition services with careful attention to neighboring properties 
                      and environmental considerations.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Full house demolition</li>
                      <li>• Partial structure removal</li>
                      <li>• Garage and outbuilding demolition</li>
                      <li>• Site cleanup and preparation</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="w-full h-48 bg-blue-50 flex items-center justify-center">
                    <i className="ri-building-2-line text-6xl text-blue-600"></i>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">Commercial Demolition</h3>
                    <p className="text-gray-600 mb-4">
                      Commercial and industrial demolition projects managed 
                      with expertise and efficiency.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Office building demolition</li>
                      <li>• Warehouse and industrial facilities</li>
                      <li>• Retail space demolition</li>
                      <li>• Multi-story structure removal</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src="/images/7cbfb6507c7927ddc781f5d0e3dcaa89.jpg"
                    alt="Interior Demolition"
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">Interior & Selective</h3>
                    <p className="text-gray-600 mb-4">
                      Precise interior demolition and selective removal for renovations 
                      and space modifications.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Wall and partition removal</li>
                      <li>• Kitchen and bathroom demolition</li>
                      <li>• Flooring and ceiling removal</li>
                      <li>• Selective structural modifications</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Laboratory Services */}
            <div className="fade-in-on-scroll">
              <div className="bg-gray-50 rounded-2xl p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">Specialized Laboratory Demolition</h2>
                    <p className="text-lg text-gray-600 mb-6">
                      Expert handling of sensitive laboratory environments requiring specialized 
                      knowledge and equipment for safe demolition and equipment removal.
                    </p>
                    <div className="space-y-4 mb-8">
                      <div className="flex items-start">
                        <i className="ri-flask-line text-blue-600 text-xl mt-1 mr-3"></i>
                        <div>
                          <h4 className="font-semibold">Laboratory Equipment Removal</h4>
                          <p className="text-gray-600 text-sm">Safe disconnection and removal of complex lab equipment</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-shield-check-line text-blue-600 text-xl mt-1 mr-3"></i>
                        <div>
                          <h4 className="font-semibold">Contamination Control</h4>
                          <p className="text-gray-600 text-sm">Proper handling of potentially hazardous materials</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-recycle-line text-blue-600 text-xl mt-1 mr-3"></i>
                        <div>
                          <h4 className="font-semibold">Proper Disposal</h4>
                          <p className="text-gray-600 text-sm">Environmentally responsible disposal of lab materials</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-80 bg-blue-50 rounded-lg flex flex-col items-center justify-center shadow-inner border border-blue-100">
                    <i className="ri-microscope-line text-8xl text-blue-600 mb-6"></i>
                    <h3 className="text-2xl font-bold text-blue-900">Laboratory Services</h3>
                  </div>
                </div>
              </div>
            </div>

            {/* General Labor */}
            <div className="fade-in-on-scroll">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">General Labor Assistance</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Professional labor services to support your construction and renovation projects.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center bg-blue-50 p-6 rounded-lg">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-broom-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">Site Cleanup</h3>
                  <p className="text-gray-600 text-sm">Complete construction site cleaning and debris removal</p>
                </div>

                <div className="text-center bg-blue-50 p-6 rounded-lg">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-truck-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">Material Moving</h3>
                  <p className="text-gray-600 text-sm">Safe transport and relocation of construction materials</p>
                </div>

                <div className="text-center bg-blue-50 p-6 rounded-lg">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-hammer-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">Site Preparation</h3>
                  <p className="text-gray-600 text-sm">Ground preparation and site readiness for new construction</p>
                </div>

                <div className="text-center bg-blue-50 p-6 rounded-lg">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-tools-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">General Support</h3>
                  <p className="text-gray-600 text-sm">Skilled labor assistance for various construction tasks</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Service Area
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We proudly serve clients throughout New England with the same level of 
              professionalism and expertise in every location.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 fade-in-on-scroll">
            <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="font-bold text-gray-900">Massachusetts</h3>
              <p className="text-sm text-gray-600">Primary Service Area</p>
            </div>
            <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="font-bold text-gray-900">Rhode Island</h3>
              <p className="text-sm text-gray-600">Full Service Coverage</p>
            </div>
            <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="font-bold text-gray-900">Connecticut</h3>
              <p className="text-sm text-gray-600">Full Service Coverage</p>
            </div>
            <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="font-bold text-gray-900">New Hampshire</h3>
              <p className="text-sm text-gray-600">Full Service Coverage</p>
            </div>
            <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="font-bold text-gray-900">Vermont</h3>
              <p className="text-sm text-gray-600">Full Service Coverage</p>
            </div>
            <div className="text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <i className="ri-map-pin-line text-3xl text-blue-600 mb-3"></i>
              <h3 className="font-bold text-gray-900">Maine</h3>
              <p className="text-sm text-gray-600">Full Service Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial consultation to project completion, we follow a systematic 
              approach that ensures safety, efficiency, and client satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center fade-in-on-scroll">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
              <h3 className="text-xl font-bold mb-3">Consultation</h3>
              <p className="text-gray-600">Free on-site assessment and project planning consultation</p>
            </div>
            <div className="text-center fade-in-on-scroll">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
              <h3 className="text-xl font-bold mb-3">Planning</h3>
              <p className="text-gray-600">Detailed project plan with timeline, permits, and safety protocols</p>
            </div>
            <div className="text-center fade-in-on-scroll">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
              <h3 className="text-xl font-bold mb-3">Execution</h3>
              <p className="text-gray-600">Professional demolition with continuous safety monitoring</p>
            </div>
            <div className="text-center fade-in-on-scroll">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">4</div>
              <h3 className="text-xl font-bold mb-3">Cleanup</h3>
              <p className="text-gray-600">Complete site cleanup and debris removal for project completion</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and detailed project assessment. 
              We're ready to bring expertise and professionalism to your demolition project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="text-xl px-10 py-5">
                  Get Free Consultation
                </Button>
              </Link>
              <a href="tel:857-777-4300" className="text-2xl hover:text-blue-400 transition-colors cursor-pointer">
                <i className="ri-phone-fill mr-2"></i>
                857-777-4300
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      {/* Removido o script Neurolead duplicado - já está no index.html */}
    </div>
  );
}
